import { useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../components/ui/PageHeader';
import FormDialogShell, { gradientButtonSx } from '../components/ui/FormDialogShell';
import ViewToggle, { useViewMode } from '../components/ui/ViewToggle';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Slider,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUploadRounded,
  ClosedCaptionRounded,
  ClosedCaptionDisabledRounded,
  DeleteOutlineRounded,
  EditRounded,
  ArrowBackRounded,
  ErrorOutlineRounded,
  Forward10Rounded,
  FullscreenExitRounded,
  FullscreenRounded,
  IosShareRounded,
  LinkRounded,
  PauseRounded,
  PlayArrowRounded,
  Replay10Rounded,
  SearchRounded,
  VideoLibraryRounded,
  VolumeDownRounded,
  VolumeMuteRounded,
  VolumeUpRounded,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  deleteVideo,
  deleteVideoSubtitle,
  getVideo,
  getVideoSharedWith,
  listChatThreads,
  listVideos,
  shareVideoWithFriends,
  unlockVideoPlayback,
  unlockVideosMenu,
  updateVideo,
  getVideoUploadJob,
  uploadVideo,
  uploadVideoSubtitle,
  VideoItem,
  VideoShareUser,
  UploadQuota,
  API,
} from '../services/api';
import { getStoredUser as getAuthUser } from '../utils/auth';
import { useSoundBoost } from '../hooks/useSoundBoost';
import SoundSettingsButton from '../components/ui/SoundSettingsButton';

const API_BASE = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/+$/, '');
const API_ORIGIN = API_BASE.replace(/\/api$/i, '');
const absoluteUrl = (u?: string) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${API_ORIGIN}${u}`;
  return `${API_ORIGIN}/${u}`;
};

const toEmbedUrl = (url: string): string => {
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&?/\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?playsinline=1&rel=0`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?playsinline=1&title=0&byline=0&portrait=0`;
  return url;
};

const getYoutubeVideoId = (url: string): string => {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&?/\s]+)/);
  return match?.[1] || '';
};

const getVimeoVideoId = (url: string): string => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match?.[1] || '';
};

const getLinkThumbnail = (url?: string): string => {
  const value = String(url || '').trim();
  if (!value) return '';
  const youtubeId = getYoutubeVideoId(value);
  if (youtubeId) return `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
  const vimeoId = getVimeoVideoId(value);
  if (vimeoId) return `https://vumbnail.com/${vimeoId}.jpg`;
  return '';
};

const displayUploader = (v?: VideoItem | null) =>
  v?.uploadedBy?.fullName || v?.uploadedBy?.username || v?.uploadedBy?.email || 'Unknown uploader';

const isMkvVideo = (video?: VideoItem | null) => {
  const mimetype = String(video?.mimetype || '').toLowerCase();
  if (mimetype === 'video/x-matroska' || mimetype === 'video/mkv') return true;
  const source = String(video?.filename || video?.fileUrl || '').toLowerCase();
  return source.endsWith('.mkv') || source.includes('.mkv?');
};

const formatBytes = (size?: number) => {
  if (!size || size <= 0) return '—';
  const mb = size / (1024 * 1024);
  if (mb < 0.1) return '<0.1 MB';
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const formatRate = (bytesPerSec: number) => {
  if (!bytesPerSec || bytesPerSec <= 0) return '0 KB/s';
  if (bytesPerSec >= 1024 * 1024) return `${(bytesPerSec / (1024 * 1024)).toFixed(2)} MB/s`;
  return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
};

const formatDuration = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const formatDate = (v?: string) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const getCurrentUser = () => {
  return getAuthUser<Record<string, any>>() || {};
};

const getQuotaLimitReachedMessage = (limit: number, kind: 'tune' | 'video') =>
  `Upload limit reached. You can upload up to ${limit} ${kind}${limit === 1 ? '' : 's'}.`;

const getFileSizeLimitMessage = (sizeLimit: number) =>
  `File too large. Please upload a file below ${sizeLimit} MB.`;

type SlimUser = {
  _id: string;
  fullName?: string;
  email?: string;
  username?: string;
};

type ChatThread = {
  _id: string;
  participants: SlimUser[];
  requestedBy?: SlimUser;
  requestedTo?: SlimUser;
  status: 'pending' | 'active' | 'declined';
};

type VideosLocationState = {
  videoAccessGranted?: boolean;
  fromPath?: string;
  sharedVideoId?: string;
};

type SeekIndicatorState = {
  direction: 'backward' | 'forward';
  amount: number;
  visible: boolean;
  key: number;
};

const VIDEO_COLORS: [string, string] = ['#EB3349', '#F45C43'];
const videoLabelSx = {
  fontWeight: 800, fontSize: 12.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 0.75,
} as const;
const videoInputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'background.paper', fontWeight: 700 },
  '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: 'rgba(235,51,73,.3)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(235,51,73,.6)' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#EB3349' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 5px rgba(235,51,73,.16)' },
} as const;
const videoCancelSx = {
  borderRadius: '999px', px: 2.5, border: '2px solid', borderColor: 'divider', color: 'text.primary',
} as const;

export default function Videos() {
  const pageSizeOptions = [5, 10, 20, 30, 50, 100];
  const defaultPageSize = 10;
  const storedUser = getCurrentUser();
  const currentUserId = typeof storedUser?._id === 'string' ? storedUser._id : '';
  const navigate = useNavigate();
  const location = useLocation();

  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [videoQuota, setVideoQuota] = useState<UploadQuota | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useViewMode('quillstack.videos.view', 'grid');
  // Per-field validation messages, shown right under each field.
  type VideoFieldErrors = { name?: string; link?: string; file?: string };
  const [uploadErrors, setUploadErrors] = useState<VideoFieldErrors>({});
  const [editErrors, setEditErrors] = useState<VideoFieldErrors>({});
  const validateVideoFile = (f: File | null): string => {
    if (!f) return '';
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) return 'Choose a video file (MP4, WebM, OGG, MOV, AVI or MKV)';
    if (videoQuota?.sizeLimit != null && f.size > videoQuota.sizeLimit * 1024 * 1024) {
      return getFileSizeLimitMessage(videoQuota.sizeLimit);
    }
    return '';
  };
  const validateVideoLink = (v: string): string => {
    const value = v.trim();
    if (!value) return 'Enter a YouTube or Vimeo link';
    try {
      const u = new URL(value);
      if (!/^https?:$/.test(u.protocol)) return 'The link must start with http:// or https://';
    } catch {
      return 'Enter a valid link, e.g. https://youtube.com/watch?v=…';
    }
    return '';
  };
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [loading, setLoading] = useState(false);
  const [menuUnlocked, setMenuUnlocked] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [serverJob, setServerJob] = useState<{ stage: string; percent: number | null } | null>(null);
  const [uploadStats, setUploadStats] = useState<{
    loaded: number;
    total: number;
    speed: number;
    remaining: number | null;
  } | null>(null);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const videoBulk = useBulkSelect();
  const [highlightedVideoId, setHighlightedVideoId] = useState<string | null>(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [videoSourceType, setVideoSourceType] = useState<'file' | 'link'>('file');
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState('');
  const [uploadSubtitleFile, setUploadSubtitleFile] = useState<File | null>(null);
  const uploadSubtitleFileInputRef = useRef<HTMLInputElement | null>(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editVideo, setEditVideo] = useState<VideoItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editLinkUrl, setEditLinkUrl] = useState('');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement | null>(null);

  // Share dialog
  const [shareOpen, setShareOpen] = useState(false);
  const [shareVideo, setShareVideo] = useState<VideoItem | null>(null);
  const [shareFriends, setShareFriends] = useState<SlimUser[]>([]);
  const [shareSelectedIds, setShareSelectedIds] = useState<string[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSending, setShareSending] = useState(false);

  // Player
  const [current, setCurrent] = useState<VideoItem | null>(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentVideoType, setCurrentVideoType] = useState<'file' | 'link'>('file');
  const [isPlaying, setIsPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubProgress, setScrubProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoBuffering, setVideoBuffering] = useState(false);
  const [playerError, setPlayerError] = useState('');
  const [currentSubtitleUrl, setCurrentSubtitleUrl] = useState('');
  // Subtitles are fetched through the API and played from a same-origin blob: <track> needs CORS on the CDN otherwise.
  const [subtitleBlobUrl, setSubtitleBlobUrl] = useState('');
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [subtitleFile, setSubtitleFile] = useState<File | null>(null);
  const [subtitleUploading, setSubtitleUploading] = useState(false);
  const subtitleFileInputRef = useRef<HTMLInputElement | null>(null);
  const [seekIndicator, setSeekIndicator] = useState<SeekIndicatorState | null>(null);
  const [linkPlayerLoading, setLinkPlayerLoading] = useState(false);
  const [linkThumbnails, setLinkThumbnails] = useState<Record<string, string>>({});
  const videoLimitReached = videoQuota?.limit != null && videoQuota.count >= videoQuota.limit;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const sound = useSoundBoost(videoRef);
  const videoWrapperRef = useRef<HTMLDivElement | null>(null);
  const controlsHideTimerRef = useRef<number | null>(null);
  const videoCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const doubleTapRef = useRef<{ time: number; direction: 'backward' | 'forward' | null }>({
    time: 0,
    direction: null,
  });
  const seekIndicatorTimerRef = useRef<number | null>(null);
  const volumeBeforeMuteRef = useRef(1);

  const navState = (location.state as VideosLocationState | null) || null;
  const sharedVideoId = typeof navState?.sharedVideoId === 'string' ? navState.sharedVideoId : '';
  // /videos/:slug keeps the open video in the URL so a refresh (or a shared link) reopens it.
  const routeVideoSlug = decodeURIComponent(location.pathname.replace(/^\/videos\/?/, '').split('/')[0] || '');
  const prevRouteVideoSlugRef = useRef('');
  const autoOpenedRouteSlugRef = useRef('');

  const load = async (term = '') => {
    setLoading(true);
    try {
      const data = await listVideos(term);
      setVideos(data.items);
      setVideoQuota(data.quota);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load videos';
      Swal.fire('Error', msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const requestMenuAccess = async () => {
    if (!currentUserId) {
      navigate('/login');
      return;
    }

    const result = await Swal.fire({
      title: 'Unlock Videos',
      text: 'Enter your login password to open the videos section.',
      icon: 'warning',
      input: 'password',
      inputLabel: 'Login password',
      inputPlaceholder: 'Enter your password',
      confirmButtonText: 'Unlock',
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: async (value) => {
        const pwd = String(value ?? '');
        if (!pwd.trim()) {
          Swal.showValidationMessage('Password is required');
          return false;
        }
        try {
          await unlockVideosMenu(pwd);
          return true;
        } catch (e: any) {
          Swal.showValidationMessage(e?.response?.data?.message || 'Incorrect password');
          return false;
        }
      },
    });

    if (!result.isConfirmed) {
      navigate('/notes');
      return;
    }

    setMenuUnlocked(true);
  };

  useEffect(() => {
    if (navState?.videoAccessGranted) {
      setMenuUnlocked(true);
      return;
    }
    requestMenuAccess();
    // Keyed on the flag, not the state object, so opening a video (which re-pushes the state) never re-prompts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navState?.videoAccessGranted]);

  useEffect(() => {
    if (!menuUnlocked) return;
    const t = setTimeout(() => { load(search); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, menuUnlocked]);

  useEffect(() => {
    if (!sharedVideoId) return;
    setSearch('');
    setPage(1);
  }, [sharedVideoId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return videos;
    return videos.filter((v) =>
      [v.title, v.originalName, v.filename, displayUploader(v)].some((field) =>
        (field || '').toLowerCase().includes(term)
      )
    );
  }, [videos, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedVideos = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page <= totalPages) return;
    setPage(totalPages);
  }, [page, totalPages]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setError('');
    setUploadErrors((p) => ({ ...p, file: validateVideoFile(f || null) || undefined }));
  };

  const closeDialog = () => {
    setUploadOpen(false);
    setTitle('');
    setFile(null);
    setLinkUrl('');
    setVideoSourceType('file');
    setError('');
    setUploadErrors({});
    setUploadSubtitleFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (uploadSubtitleFileInputRef.current) uploadSubtitleFileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (videoLimitReached && videoQuota?.limit != null) {
      setError(getQuotaLimitReachedMessage(videoQuota.limit, 'video'));
      return;
    }
    const errs: VideoFieldErrors = {};
    if (!title.trim()) errs.name = 'Video name is required';
    else if (title.trim().length > 100) errs.name = 'Video name must be 100 characters or fewer';
    if (videoSourceType === 'file') {
      if (!file) errs.file = 'Select a video file to upload';
      else {
        const fe = validateVideoFile(file);
        if (fe) errs.file = fe;
      }
    } else {
      const le = validateVideoLink(linkUrl);
      if (le) errs.link = le;
    }
    setUploadErrors(errs);
    if (Object.keys(errs).length) return;
    setUploading(true);
    setUploadStats(null);
    const startedAt = Date.now();
    let lastAt = startedAt;
    let lastLoaded = 0;
    let smoothed = 0;
    try {
      const created = await uploadVideo(
        {
          title: title.trim(),
          videoType: videoSourceType,
          ...(videoSourceType === 'file' ? { file: file! } : { linkUrl: linkUrl.trim() }),
        },
        (loaded, total) => {
          const now = Date.now();
          const dt = (now - lastAt) / 1000;
          if (dt >= 0.5 || loaded >= total) {
            const instant = dt > 0 ? (loaded - lastLoaded) / dt : 0;
            smoothed = smoothed ? smoothed * 0.7 + instant * 0.3 : instant;
            lastAt = now;
            lastLoaded = loaded;
          }
          const remaining = smoothed > 0 ? (total - loaded) / smoothed : null;
          setUploadStats({ loaded, total, speed: smoothed, remaining });
        }
      );
      let videoItem = created;
      if (created?.jobId) {
        videoItem = await waitForServerJob(created.jobId);
      }
      if (videoSourceType === 'file' && uploadSubtitleFile) {
        try {
          const subResult = await uploadVideoSubtitle(videoItem._id, uploadSubtitleFile);
          videoItem = { ...videoItem, subtitleUrl: subResult.subtitleUrl || '' };
        } catch {
          // subtitle upload failed; proceed without subtitle
        }
      }
      setVideos((prev) => [videoItem, ...prev]);
      setPage(1);
      setVideoQuota((prev) => prev ? { ...prev, count: prev.count + 1 } : prev);
      closeDialog();
      Swal.fire('Added', 'Video added successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to add video';
      setError(msg);
      Swal.fire('Error', msg, 'error');
    } finally {
      setUploading(false);
      setUploadStats(null);
      setServerJob(null);
    }
  };

  // The server keeps working after the bytes arrive (codec check, conversion, storage save).
  // Poll its job until it reports done/error so the dialog can show real progress.
  const waitForServerJob = async (jobId: string) => {
    let failures = 0;
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        const job = await getVideoUploadJob(jobId);
        failures = 0;
        setServerJob({ stage: job.stage, percent: job.percent });
        if (job.stage === 'done' && job.video) return job.video;
        if (job.stage === 'error') throw new Error(job.error || 'Failed to add video');
      } catch (e: any) {
        if (e?.response?.status === 404 || ++failures >= 5) {
          throw e?.response?.data?.message ? e : new Error(e?.message || 'Lost connection to the server');
        }
      }
    }
  };

  const canModifyVideo = (video: VideoItem) =>
    !!currentUserId && String(video.uploadedBy?._id || '') === currentUserId;

  const getChatFriend = (thread: ChatThread) => {
    const other = thread.participants?.find((participant) => participant._id !== currentUserId);
    return other || thread.requestedBy || thread.requestedTo || null;
  };

  const displayFriendName = (friend?: SlimUser | VideoShareUser | null) =>
    friend?.fullName || friend?.username || friend?.email || 'Unknown user';

  const openShareDialog = async (video: VideoItem) => {
    setShareVideo(video);
    setShareOpen(true);
    setShareLoading(true);
    setShareFriends([]);
    setShareSelectedIds([]);
    try {
      const [threadsData, sharedWithData] = await Promise.all([
        listChatThreads(),
        getVideoSharedWith(video._id),
      ]);

      const threads: ChatThread[] = Array.isArray(threadsData?.threads) ? threadsData.threads : [];
      const friendMap = new Map<string, SlimUser>();

      for (const thread of threads) {
        const friend = getChatFriend(thread);
        if (friend?._id) {
          friendMap.set(friend._id, friend);
        }
      }

      const friends = Array.from(friendMap.values());
      const sharedWith = Array.isArray(sharedWithData?.sharedWith) ? sharedWithData.sharedWith : [];
      setShareFriends(friends);
      setShareSelectedIds(
        sharedWith
          .map((friend) => friend?._id || '')
          .filter((id) => id && friendMap.has(id))
      );
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load shared friends';
      Swal.fire('Error', msg, 'error');
    } finally {
      setShareLoading(false);
    }
  };

  const closeShareDialog = () => {
    setShareOpen(false);
    setShareVideo(null);
    setShareFriends([]);
    setShareSelectedIds([]);
    setShareLoading(false);
    setShareSending(false);
  };

  const toggleShareFriend = (friendId: string) => {
    setShareSelectedIds((prev) =>
      prev.includes(friendId) ? prev.filter((id) => id !== friendId) : [...prev, friendId]
    );
  };

  const handleShareVideo = async () => {
    if (!shareVideo) return;

    setShareSending(true);
    try {
      const response = await shareVideoWithFriends(shareVideo._id, shareSelectedIds);
      setVideos((prev) =>
        prev.map((video) =>
          video._id === shareVideo._id ? { ...video, sharedWith: response.sharedWith || [] } : video
        )
      );
      closeShareDialog();
      Swal.fire(
        'Saved',
        shareSelectedIds.length
          ? 'Selected friends can now see this video in their Videos page.'
          : 'This video is no longer shared with any friends.',
        'success'
      );
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to update shared friends';
      Swal.fire('Error', msg, 'error');
      setShareSending(false);
    }
  };

  const openEditDialog = async (video: VideoItem) => {
    setEditVideo(video);
    setEditTitle(video.title);
    setEditLinkUrl('');
    setEditFile(null);
    setEditError('');
    if (editFileInputRef.current) editFileInputRef.current.value = '';
    setEditOpen(true);

    if ((video.videoType || 'file') === 'link') {
      setEditLoading(true);
      try {
        const detail = await getVideo(video._id);
        setEditLinkUrl(detail.linkUrl || '');
      } catch {
        // leave blank — user can type new URL
      } finally {
        setEditLoading(false);
      }
    }
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditVideo(null);
    setEditTitle('');
    setEditLinkUrl('');
    setEditFile(null);
    setEditError('');
    setEditErrors({});
    setSubtitleFile(null);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
    if (subtitleFileInputRef.current) subtitleFileInputRef.current.value = '';
  };

  const handleEditSave = async () => {
    if (!editVideo) return;
    const eErrs: VideoFieldErrors = {};
    if (!editTitle.trim()) eErrs.name = 'Video name is required';
    else if (editTitle.trim().length > 100) eErrs.name = 'Video name must be 100 characters or fewer';
    if ((editVideo.videoType || 'file') === 'link') {
      if (editLinkUrl.trim()) {
        const le = validateVideoLink(editLinkUrl);
        if (le) eErrs.link = le;
      }
    } else if (editFile) {
      const fe = validateVideoFile(editFile);
      if (fe) eErrs.file = fe;
    }
    setEditErrors(eErrs);
    if (Object.keys(eErrs).length) return;
    setSaving(true);
    try {
      const payload: { title?: string; linkUrl?: string; file?: File } = { title: editTitle.trim() };
      if ((editVideo.videoType || 'file') === 'link') {
        if (editLinkUrl.trim()) payload.linkUrl = editLinkUrl.trim();
      } else if (editFile) {
        payload.file = editFile;
      }
      const updated = await updateVideo(editVideo._id, payload);
      setVideos((prev) =>
        prev.map((v) => (v._id === editVideo._id ? { ...v, ...updated } : v))
      );
      if (current?._id === editVideo._id) {
        setCurrent((prev) => prev ? { ...prev, ...updated } : prev);
      }
      closeEditDialog();
      Swal.fire('Saved', 'Video updated successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to update video';
      setEditError(msg);
      Swal.fire('Error', msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVideo = async (video: VideoItem) => {
    const result = await Swal.fire({
      title: 'Delete video?',
      text: 'This will permanently delete the video from storage.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    setDeletingVideoId(video._id);
    try {
      await deleteVideo(video._id);
      if (current?._id === video._id) closePlayer();
      setVideos((prev) => prev.filter((item) => item._id !== video._id));
      setVideoQuota((prev) => prev ? { ...prev, count: Math.max(0, prev.count - 1) } : prev);
      Swal.fire('Deleted', 'Video deleted successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete video';
      Swal.fire('Error', msg, 'error');
    } finally {
      setDeletingVideoId(null);
    }
  };

  // Called by the bulk-delete bar after deleteVideo() succeeded for these ids.
  const handleBulkVideosDeleted = (ids: string[]) => {
    if (!ids.length) return;
    if (current && ids.includes(current._id)) closePlayer();
    setVideos((prev) => prev.filter((item) => !ids.includes(item._id)));
    setVideoQuota((prev) => (prev ? { ...prev, count: Math.max(0, prev.count - ids.length) } : prev));
  };

  const handlePlay = async (video: VideoItem) => {
    const player = videoRef.current;
    const vType = video.videoType || 'file';

    if (current?._id === video._id && vType === 'file' && player && currentUrl) {
      setPlayerError('');
      setVideoBuffering(false);
      if (player.paused) {
        ensurePlayerHasSound(player);
        player.play().catch(() => {});
      } else {
        player.pause();
      }
      return;
    }

    if (current?._id === video._id && vType === 'link') {
      closePlayer();
      return;
    }

    // The password is asked once when the Videos section opens, so a video starts straight away.
    // MKV files are converted on first play, which can take a while, so show progress for those.
    const preparing = isMkvVideo(video);
    if (preparing) {
      Swal.fire({
        title: 'Preparing video',
        text: 'MKV videos may take some time the first time they are prepared for playback.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });
    }

    let data: Awaited<ReturnType<typeof unlockVideoPlayback>>;
    try {
      data = await unlockVideoPlayback(video._id);
    } catch (e: any) {
      if (preparing) Swal.close();
      Swal.fire('Error', e?.response?.data?.message || 'Unable to open video', 'error');
      return false;
    }
    if (preparing) Swal.close();

    if (routeVideoSlug !== (video.slug || video._id)) {
      navigate(`/videos/${video.slug || video._id}`, { state: location.state });
    }

    const resolvedType = data.videoType || vType;

    if (player) {
      player.pause();
      player.currentTime = 0;
    }

    setCurrent(video);
    setCurrentVideoType(resolvedType as 'file' | 'link');
    setCurrentUrl(resolvedType === 'link' ? (data.linkUrl || '') : (data.fileUrl || ''));
    setCurrentSubtitleUrl(resolvedType === 'file' ? (data.subtitleUrl || '') : '');
    setSubtitlesEnabled(false);
    setIsPlaying(false);
    setControlsVisible(true);
    setVideoProgress(0);
    setScrubProgress(0);
    setIsScrubbing(false);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setPlayerError('');
    setVideoBuffering(resolvedType === 'file');
    setLinkPlayerLoading(resolvedType === 'link');

    if (resolvedType === 'file') {
      const restoredVolume = videoVolume > 0 ? videoVolume : 1;
      volumeBeforeMuteRef.current = restoredVolume;
      setVideoMuted(false);
      setVideoVolume(restoredVolume);
    }
    return true;
  };

  const closePlayer = () => {
    if (routeVideoSlug) navigate('/videos', { state: location.state });
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    if (seekIndicatorTimerRef.current) clearTimeout(seekIndicatorTimerRef.current);
    setCurrent(null);
    setCurrentUrl('');
    setIsPlaying(false);
    setVideoProgress(0);
    setScrubProgress(0);
    setIsScrubbing(false);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setControlsVisible(true);
    setIsFullscreen(false);
    setVideoBuffering(false);
    setPlayerError('');
    setSeekIndicator(null);
    setLinkPlayerLoading(false);
    setVideoMuted(false);
    setVideoVolume(1);
    volumeBeforeMuteRef.current = 1;
    setCurrentSubtitleUrl('');
    setSubtitlesEnabled(false);
  };

  // Browser back from /videos/:id to /videos closes the player.
  useEffect(() => {
    const prev = prevRouteVideoSlugRef.current;
    prevRouteVideoSlugRef.current = routeVideoSlug;
    if (prev && !routeVideoSlug && current) closePlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeVideoSlug]);

  // Landing on /videos/:id directly (refresh, shared link): load that video and ask for the password to play it.
  useEffect(() => {
    if (!routeVideoSlug || !menuUnlocked) return;
    if (current?.slug === routeVideoSlug || current?._id === routeVideoSlug || autoOpenedRouteSlugRef.current === routeVideoSlug) return;
    autoOpenedRouteSlugRef.current = routeVideoSlug;
    (async () => {
      try {
        const video = await getVideo(routeVideoSlug);
        const opened = await handlePlay(video);
        if (!opened) navigate('/videos', { replace: true, state: location.state });
      } catch (e: any) {
        Swal.fire('Error', e?.response?.data?.message || 'Unable to open this video', 'error');
        navigate('/videos', { replace: true, state: location.state });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeVideoSlug, menuUnlocked]);

  useEffect(() => {
    if (!currentSubtitleUrl) {
      setSubtitleBlobUrl('');
      return;
    }
    let cancelled = false;
    let objectUrl = '';
    API.get('/videos/subtitle-proxy', { params: { url: currentSubtitleUrl }, responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'text/vtt' }));
        setSubtitleBlobUrl(objectUrl);
      })
      .catch(() => !cancelled && setSubtitleBlobUrl(''));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [currentSubtitleUrl]);

  const startControlsHideTimer = () => {
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    if (currentVideoType !== 'file' || !isPlaying || playerError) return;
    controlsHideTimerRef.current = window.setTimeout(() => setControlsVisible(false), 3200);
  };

  const showControlsTemporarily = () => {
    setControlsVisible(true);
    startControlsHideTimer();
  };

  const ensurePlayerHasSound = (player: HTMLVideoElement) => {
    const restoredVolume = videoVolume > 0 ? videoVolume : (volumeBeforeMuteRef.current > 0 ? volumeBeforeMuteRef.current : 1);
    player.muted = false;
    player.defaultMuted = false;
    player.volume = restoredVolume;
    volumeBeforeMuteRef.current = restoredVolume;
    setVideoMuted(false);
    setVideoVolume(restoredVolume);
  };

  const togglePlayPause = () => {
    const player = videoRef.current;
    if (!player) return;
    setPlayerError('');
    if (player.paused) {
      ensurePlayerHasSound(player);
      player.play().catch(() => {});
    }
    else player.pause();
    showControlsTemporarily();
  };

  const toggleFullscreen = () => {
    const wrapper = videoWrapperRef.current;
    if (!wrapper) return;
    if (!document.fullscreenElement) {
      wrapper.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  const flashSeekIndicator = (direction: 'backward' | 'forward', amount: number) => {
    if (seekIndicatorTimerRef.current) clearTimeout(seekIndicatorTimerRef.current);
    setSeekIndicator({ direction, amount, visible: true, key: Date.now() });
    seekIndicatorTimerRef.current = window.setTimeout(() => {
      setSeekIndicator((prev) => (prev ? { ...prev, visible: false } : prev));
    }, 700);
  };

  const seekBy = (seconds: number) => {
    const player = videoRef.current;
    if (!player || !player.duration) return;
    const nextTime = Math.min(Math.max(player.currentTime + seconds, 0), player.duration);
    player.currentTime = nextTime;
    setVideoCurrentTime(nextTime);
    setVideoProgress(player.duration ? (nextTime / player.duration) * 100 : 0);
    setScrubProgress(player.duration ? (nextTime / player.duration) * 100 : 0);
    flashSeekIndicator(seconds < 0 ? 'backward' : 'forward', Math.abs(seconds));
    showControlsTemporarily();
  };

  const handleSeekChange = (_: Event, val: number | number[]) => {
    const nextValue = Array.isArray(val) ? val[0] : val;
    setIsScrubbing(true);
    setScrubProgress(nextValue);
    setControlsVisible(true);
  };

  const handleSeekCommit = (_: Event | React.SyntheticEvent, val: number | number[]) => {
    const player = videoRef.current;
    const nextValue = Array.isArray(val) ? val[0] : val;
    if (player && player.duration) {
      const nextTime = (nextValue / 100) * player.duration;
      player.currentTime = nextTime;
      setVideoCurrentTime(nextTime);
    }
    setVideoProgress(nextValue);
    setScrubProgress(nextValue);
    setIsScrubbing(false);
    showControlsTemporarily();
  };

  const handleVolumeChange = (_: Event, val: number | number[]) => {
    const player = videoRef.current;
    if (!player) return;
    const v = (val as number) / 100;
    player.volume = v;
    player.muted = v === 0;
    setVideoVolume(v);
    setVideoMuted(v === 0);
    if (v > 0) volumeBeforeMuteRef.current = v;
    showControlsTemporarily();
  };

  const toggleMute = () => {
    const player = videoRef.current;
    if (!player) return;
    if (videoMuted) {
      const restored = volumeBeforeMuteRef.current > 0 ? volumeBeforeMuteRef.current : 1;
      player.volume = restored;
      player.muted = false;
      setVideoMuted(false);
      setVideoVolume(restored);
    } else {
      volumeBeforeMuteRef.current = videoVolume > 0 ? videoVolume : volumeBeforeMuteRef.current;
      player.muted = true;
      setVideoMuted(true);
    }
    showControlsTemporarily();
  };

  const formatTime = (secs: number) => {
    if (!secs || isNaN(secs)) return '0:00';
    const total = Math.floor(secs);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const getVolumeIcon = () => {
    if (videoMuted || videoVolume === 0) return <VolumeMuteRounded fontSize="small" />;
    if (videoVolume <= 0.45) return <VolumeDownRounded fontSize="small" />;
    return <VolumeUpRounded fontSize="small" />;
  };

  const handlePlayerSurfaceClick = () => {
    if (currentVideoType !== 'file') return;
    if (controlsVisible && isPlaying) {
      setControlsVisible(false);
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
      return;
    }
    showControlsTemporarily();
  };

  const handleDoubleTapGesture = (clientX: number) => {
    const wrapper = videoWrapperRef.current;
    if (!wrapper) return;
    const bounds = wrapper.getBoundingClientRect();
    const direction = clientX - bounds.left < bounds.width / 2 ? 'backward' : 'forward';
    const now = Date.now();
    const isDoubleTap = now - doubleTapRef.current.time < 280 && doubleTapRef.current.direction === direction;

    doubleTapRef.current = { time: now, direction };

    if (!isDoubleTap) {
      showControlsTemporarily();
      return;
    }

    seekBy(direction === 'backward' ? -10 : 10);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    handleDoubleTapGesture(touch.clientX);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    handleDoubleTapGesture(event.clientX);
  };

  useEffect(() => {
    const onFsChange = () => {
      const inFs = !!document.fullscreenElement;
      setIsFullscreen(inFs);
      if (inFs) {
        showControlsTemporarily();
      } else {
        if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
        setControlsVisible(true);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
      if (seekIndicatorTimerRef.current) clearTimeout(seekIndicatorTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    startControlsHideTimer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentVideoType, playerError]);

  // Keyboard: Space plays/pauses, Left/Right seek 10s - only while the file player is open.
  const playerKeysRef = useRef<{ toggle: () => void; seek: (seconds: number) => void }>({ toggle: () => {}, seek: () => {} });
  playerKeysRef.current = { toggle: togglePlayPause, seek: (seconds) => seekBy(seconds) };
  const playerOpen = !!current && !!currentUrl && currentVideoType === 'file';
  useEffect(() => {
    if (!playerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault(); // also stops a focused button from re-triggering on Space
        if (!e.repeat) playerKeysRef.current.toggle();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        playerKeysRef.current.seek(10);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        playerKeysRef.current.seek(-10);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playerOpen]);

  useEffect(() => {
    const player = videoRef.current;
    if (!player || currentVideoType !== 'file' || !currentUrl) return;
    player.muted = videoMuted;
    player.volume = videoMuted ? 0 : videoVolume;
  }, [currentUrl, currentVideoType, videoMuted, videoVolume]);

  useEffect(() => {
    const player = videoRef.current;
    if (!player) return;
    const tracks = Array.from(player.textTracks);
    tracks.forEach((t) => { t.mode = subtitlesEnabled ? 'showing' : 'hidden'; });
  }, [subtitlesEnabled, currentSubtitleUrl, subtitleBlobUrl]);

  useEffect(() => {
    if (!current) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [current]);

  useEffect(() => {
    const targets = videos.filter(
      (video) => (video.videoType || 'file') === 'link' && canModifyVideo(video) && !linkThumbnails[video._id]
    );
    if (!targets.length) return;

    let cancelled = false;
    targets.forEach(async (video) => {
      try {
        const detail = await getVideo(video._id);
        const thumb = getLinkThumbnail(detail.linkUrl);
        if (!thumb || cancelled) return;
        setLinkThumbnails((prev) => (prev[video._id] ? prev : { ...prev, [video._id]: thumb }));
      } catch {
        // keep generic scaffold if the protected link URL is not available
      }
    });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, currentUserId, linkThumbnails]);

  const getPlayButtonLabel = (video: VideoItem) => {
    if (current?._id !== video._id) return 'Play Video';
    if (currentVideoType === 'link') return 'Close Player';
    return isPlaying ? 'Pause Video' : 'Resume Video';
  };

  useEffect(() => {
    if (!menuUnlocked || loading || !sharedVideoId) return;
    const matchIndex = filtered.findIndex((video) => video._id === sharedVideoId);
    if (matchIndex === -1) return;

    const targetPage = Math.floor(matchIndex / pageSize) + 1;
    if (page !== targetPage) {
      setPage(targetPage);
      return;
    }

    const node = videoCardRefs.current[sharedVideoId];
    if (!node) return;

    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedVideoId(sharedVideoId);

    const timeoutId = window.setTimeout(() => {
      setHighlightedVideoId((prev) => (prev === sharedVideoId ? null : prev));
    }, 2600);

    return () => window.clearTimeout(timeoutId);
  }, [filtered, loading, menuUnlocked, page, pageSize, sharedVideoId]);

  return (
    <Box>
      <PageHeader
        title="Videos"
        subtitle="Enter your login password once to open this menu and play your protected videos."
        actions={
          <>
            {menuUnlocked && videoQuota?.limit != null && (
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: videoQuota.count >= videoQuota.limit ? '#FFD1D9' : 'rgba(255,255,255,.9)' }}
              >
                {videoQuota.count} / {videoQuota.limit} videos
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={<CloudUploadRounded />}
              onClick={() => { setUploadOpen(true); setError(''); }}
              disabled={!menuUnlocked || videoLimitReached}
            >
              Add Video
            </Button>
          </>
        }
      />

      {!menuUnlocked && <LinearProgress sx={{ mb: 2 }} />}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search videos by name or uploader…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={!menuUnlocked}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <ViewToggle value={viewMode} onChange={setViewMode} />
          <FormControl size="small" sx={{ minWidth: 140, flex: { xs: '1 1 auto', md: '0 0 140px' } }}>
            <InputLabel>Per page</InputLabel>
            <Select
              label="Per page"
              value={pageSize}
              disabled={!menuUnlocked}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {loading && menuUnlocked && <LinearProgress sx={{ mt: 1 }} />}
      </Paper>

      <Box
        sx={viewMode === 'grid'
          ? { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' } }
          : { display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Paper key={idx} sx={{ p: 1.25, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Skeleton variant="rounded" width={148} height={84} sx={{ flexShrink: 0, maxWidth: '100%' }} />
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Skeleton height={28} width="58%" />
                  <Skeleton height={18} width="36%" />
                  <Skeleton height={18} width="72%" />
                  <Skeleton variant="rounded" height={34} width={148} />
                </Stack>
              </Stack>
            </Paper>
          ))
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', gridColumn: '1 / -1' }}>
            <Typography color="text.secondary">
              No videos found. Add your first protected video to get started.
            </Typography>
          </Paper>
        ) : (
          [
          <Box key="video-bulk-bar" sx={{ gridColumn: '1 / -1' }}>
            <BulkSelectBar
              bulk={videoBulk}
              visibleIds={paginatedVideos.filter(canModifyVideo).map((v) => v._id)}
              noun="video"
              deleteOne={deleteVideo}
              onDone={handleBulkVideosDeleted}
            />
          </Box>,
          ...paginatedVideos.map((video) => {
            const isCurrent = current?._id === video._id;
            const vType = video.videoType || 'file';
            const thumbUrl = vType === 'link' ? linkThumbnails[video._id] || '' : '';
            return (
              <Paper
                key={video._id}
                ref={(node) => { videoCardRefs.current[video._id] = node; }}
                sx={{
                  p: 1.25,
                  borderRadius: '24px',
                  overflow: 'hidden',
                  border: '1px solid rgba(235, 51, 73, 0.18)',
                  boxShadow: highlightedVideoId === video._id
                    ? '0 0 0 3px rgba(235, 51, 73, 0.25)'
                    : '0 14px 30px rgba(235, 51, 73, 0.14)',
                  transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 24px 44px rgba(235, 51, 73, 0.28)',
                  },
                  '&:hover .qs-play-orb': { transform: 'scale(1.12)' },
                }}
              >
                <Stack direction={viewMode === 'grid' ? 'column' : { xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Box
                    onClick={() => handlePlay(video)}
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      width: viewMode === 'grid' ? '100%' : { xs: '100%', sm: 190 },
                      minWidth: viewMode === 'grid' ? undefined : { sm: 190 },
                      aspectRatio: '16 / 9',
                      borderRadius: '18px',
                      overflow: 'hidden',
                      background: 'linear-gradient(160deg, #020617 0%, #111827 72%, #1f2937 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {thumbUrl ? (
                      <Box
                        component="img"
                        src={thumbUrl}
                        alt={video.title || 'Video thumbnail'}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Stack spacing={0.75} alignItems="center" sx={{ px: 1.5, textAlign: 'center' }}>
                        {vType === 'link' ? (
                          <LinkRounded sx={{ color: '#fff', fontSize: 28 }} />
                        ) : (
                          <VideoLibraryRounded sx={{ color: '#fff', fontSize: 30 }} />
                        )}
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                          {vType === 'link' ? 'Protected link preview' : 'Protected file preview'}
                        </Typography>
                      </Stack>
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.06), rgba(0,0,0,0.68))',
                      }}
                    />
                    <Box
                      className="qs-play-orb"
                      sx={{
                        position: 'absolute', top: '50%', left: '50%', width: 54, height: 54, mt: '-27px', ml: '-27px',
                        borderRadius: '50%', display: 'grid', placeItems: 'center', color: '#fff',
                        background: 'linear-gradient(135deg, #EB3349, #F45C43)',
                        boxShadow: '0 10px 24px rgba(235,51,73,.55), inset 0 1px 0 rgba(255,255,255,.5)',
                        border: '1px solid rgba(255,255,255,.6)',
                        transition: 'transform .25s ease',
                      }}
                    >
                      <PlayArrowRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ position: 'absolute', left: 10, right: 10, bottom: 10 }}
                    >
                      <Chip
                        label={vType === 'link' ? 'Link' : 'File'}
                        size="small"
                        icon={vType === 'link' ? <LinkRounded /> : undefined}
                        sx={{
                          height: 24,
                          bgcolor: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                      {vType === 'file' && (
                        <Chip
                          label={formatBytes(video.size)}
                          size="small"
                          sx={{ height: 24, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                      direction={viewMode === 'grid' ? 'column' : { xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={800} noWrap>
                          {video.title || 'Untitled video'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {formatDate(video.createdAt)}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {!canModifyVideo(video) && (
                          <Chip label="Shared with you" size="small" color="success" variant="outlined" />
                        )}
                        {!!video.sharedWith?.length && canModifyVideo(video) && (
                          <Chip
                            label={`Shared ${video.sharedWith.length}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        {canModifyVideo(video) && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openShareDialog(video)}
                              aria-label="Share video with friends"
                            >
                              <IosShareRounded fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDialog(video)}
                              aria-label="Edit video"
                            >
                              <EditRounded fontSize="small" />
                            </IconButton>
                            <Checkbox
                              size="small"
                              checked={videoBulk.isSelected(video._id)}
                              onChange={() => videoBulk.toggle(video._id)}
                              inputProps={{ 'aria-label': 'Select video' }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteVideo(video)}
                              disabled={deletingVideoId === video._id}
                              aria-label="Delete video"
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" noWrap>
                      {vType === 'file'
                        ? video.originalName || video.filename || 'Protected upload'
                        : 'YouTube or Vimeo protected link'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Uploaded by {displayUploader(video)}
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75}>
                      <Button
                        variant={isCurrent ? 'outlined' : 'contained'}
                        startIcon={<PlayArrowRounded />}
                        onClick={() => handlePlay(video)}
                        sx={{ minWidth: 140, py: 0.65 }}
                      >
                        {getPlayButtonLabel(video)}
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            );
          }),
          ]
        )}
      </Box>

      {menuUnlocked && !loading && filtered.length > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_event, nextPage) => setPage(nextPage)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Upload / Add Dialog */}
      <FormDialogShell
        open={uploadOpen}
        onClose={closeDialog}
        colors={VIDEO_COLORS}
        maxWidth="md"
        icon={<VideoLibraryRounded />}
        title="Add protected video"
        subtitle="Upload a file or paste a YouTube / Vimeo link"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeDialog} sx={videoCancelSx}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !!error}
              startIcon={<CloudUploadRounded />}
              sx={gradientButtonSx(VIDEO_COLORS)}
            >
              {uploading ? 'Uploading…' : 'Add Video'}
            </Button>
          </>
        }
      >
          <Stack spacing={2.25} sx={{ minWidth: 0 }}>
            {/* source type: two big option tiles */}
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: '1fr 1fr' }}>
              {([
                { v: 'file', label: 'Upload file', hint: 'MP4, WebM, MOV, MKV…', Icon: CloudUploadRounded },
                { v: 'link', label: 'YouTube / Vimeo', hint: 'Paste a link', Icon: LinkRounded },
              ] as const).map(({ v, label, hint, Icon }) => {
                const active = videoSourceType === v;
                return (
                  <Box
                    key={v}
                    component="button"
                    type="button"
                    onClick={() => { setVideoSourceType(v); setError(''); setUploadErrors({}); }}
                    sx={{
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', p: 1.5, borderRadius: '18px',
                      display: 'flex', alignItems: 'center', gap: 1.25,
                      border: '2px solid', borderColor: active ? '#EB3349' : 'rgba(235,51,73,.25)',
                      color: active ? '#fff' : 'text.primary',
                      background: active ? 'linear-gradient(135deg,#EB3349,#F45C43)' : 'transparent',
                      boxShadow: active ? '0 12px 24px rgba(235,51,73,.35), inset 0 1px 0 rgba(255,255,255,.4)' : 'none',
                      transition: 'all .18s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 42, height: 42, borderRadius: '14px', display: 'grid', placeItems: 'center', flexShrink: 0,
                        color: active ? '#fff' : '#EB3349',
                        bgcolor: active ? 'rgba(255,255,255,.25)' : 'rgba(235,51,73,.1)',
                      }}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }}>{label}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, opacity: active ? 0.95 : 0.7 }}>{hint}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box>
              <Typography sx={videoLabelSx}>Video name</Typography>
              <TextField
                placeholder="Enter a name"
                fullWidth
                value={title}
                onChange={(e) => { setTitle(e.target.value); setUploadErrors((p) => ({ ...p, name: undefined })); }}
                error={!!uploadErrors.name}
                helperText={uploadErrors.name}
                sx={videoInputSx}
              />
            </Box>

            {videoSourceType === 'link' ? (
              <Box>
                <Typography sx={videoLabelSx}>Video URL</Typography>
                <TextField
                  placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
                  fullWidth
                  value={linkUrl}
                  onChange={(e) => { setLinkUrl(e.target.value); setError(''); setUploadErrors((p) => ({ ...p, link: undefined })); }}
                  error={!!uploadErrors.link}
                  helperText={uploadErrors.link}
                  sx={videoInputSx}
                />
              </Box>
            ) : (
              <>
                <Box
                  component="label"
                  sx={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, p: 2.25, borderRadius: '20px',
                    border: '2px dashed', borderColor: file ? '#EB3349' : 'rgba(235,51,73,.45)',
                    bgcolor: file ? 'rgba(235,51,73,.07)' : 'transparent',
                    transition: 'all .2s ease',
                    '&:hover': { bgcolor: 'rgba(235,51,73,.07)', borderColor: '#EB3349' },
                  }}
                >
                  <Box
                    sx={{
                      width: 56, height: 56, borderRadius: '18px', display: 'grid', placeItems: 'center', color: '#fff', flexShrink: 0,
                      background: 'linear-gradient(135deg,#EB3349,#F45C43)',
                      boxShadow: '0 10px 20px rgba(235,51,73,.4), inset 0 1px 0 rgba(255,255,255,.45)',
                    }}
                  >
                    {file ? <VideoLibraryRounded /> : <CloudUploadRounded />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 900, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {file ? file.name : 'Choose a video file'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {file
                        ? 'Click to choose a different file'
                        : videoQuota?.sizeLimit != null ? `Up to ${videoQuota.sizeLimit} MB` : 'MP4 or WebM play best in the browser'}
                    </Typography>
                  </Box>
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                    onChange={onFileChange}
                  />
                </Box>
                {uploadErrors.file && (
                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mt: -1 }}>{uploadErrors.file}</Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                  Recommended for browser playback: MP4 or WebM. MKV uploads are converted automatically.
                </Typography>

                <Box
                  component="label"
                  sx={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.25, borderRadius: '16px',
                    border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                    '&:hover': { borderColor: '#EB3349' },
                  }}
                >
                  <ClosedCaptionRounded sx={{ color: '#EB3349' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800 }}>Subtitles (optional)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {uploadSubtitleFile ? uploadSubtitleFile.name : 'Add a .srt or .vtt file'}
                    </Typography>
                  </Box>
                  <input
                    ref={uploadSubtitleFileInputRef}
                    hidden
                    type="file"
                    accept=".srt,.vtt"
                    onChange={(e) => setUploadSubtitleFile(e.target.files?.[0] || null)}
                  />
                  {uploadSubtitleFile && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        setUploadSubtitleFile(null);
                        if (uploadSubtitleFileInputRef.current) uploadSubtitleFileInputRef.current.value = '';
                      }}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </>
            )}

            {uploading && uploadStats && uploadStats.total > 0 && (() => {
              const pct = Math.min(100, (uploadStats.loaded / uploadStats.total) * 100);
              const sent = uploadStats.loaded >= uploadStats.total;
              const stageLabels: Record<string, string> = {
                queued: 'Waiting for server…',
                checking: 'Step 2/3 · Checking video format…',
                converting: 'Step 2/3 · Converting for browser playback',
                saving: 'Step 3/3 · Saving to storage',
              };
              const serverPct = serverJob?.percent;
              return (
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {!sent
                        ? `Step 1/3 · Uploading ${pct.toFixed(1)}%`
                        : `${stageLabels[serverJob?.stage || 'queued'] || 'Processing…'}${serverPct != null ? ` ${serverPct.toFixed(0)}%` : ''}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatBytes(uploadStats.loaded)} / {formatBytes(uploadStats.total)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant={!sent ? 'determinate' : serverPct != null ? 'determinate' : 'indeterminate'}
                    value={!sent ? pct : serverPct ?? 0}
                    sx={{
                      height: 12, borderRadius: 999, bgcolor: 'rgba(235,51,73,.14)',
                      '& .MuiLinearProgress-bar': { borderRadius: 999, background: 'linear-gradient(90deg,#EB3349,#F45C43)' },
                    }}
                  />
                  {!sent && (
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Speed: {formatRate(uploadStats.speed)} ({((uploadStats.speed * 8) / 1_000_000).toFixed(2)} Mbps)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {uploadStats.remaining != null ? `${formatDuration(uploadStats.remaining)} left` : 'Calculating…'}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              );
            })()}

            {error && (
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
                {error}
              </Typography>
            )}
          </Stack>
      </FormDialogShell>

      {/* Edit Dialog */}
      <FormDialogShell
        open={editOpen}
        onClose={closeEditDialog}
        colors={VIDEO_COLORS}
        maxWidth="md"
        icon={<EditRounded />}
        title="Edit video"
        subtitle="Rename it, replace the file or manage subtitles"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeEditDialog} sx={videoCancelSx}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving} sx={gradientButtonSx(VIDEO_COLORS)}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </>
        }
      >
          <Stack spacing={2.25} sx={{ minWidth: 0 }}>
            <Box>
              <Typography sx={videoLabelSx}>Video name</Typography>
              <TextField
                fullWidth
                value={editTitle}
                onChange={(e) => { setEditTitle(e.target.value); setEditErrors((p) => ({ ...p, name: undefined })); }}
                error={!!editErrors.name}
                helperText={editErrors.name}
                sx={videoInputSx}
              />
            </Box>
            {(editVideo?.videoType || 'file') === 'link' ? (
              <Box>
                {editLoading && <LinearProgress sx={{ mb: 1, borderRadius: 999 }} />}
                <Typography sx={videoLabelSx}>Video URL</Typography>
                <TextField
                  placeholder="https://youtube.com/watch?v=… or https://vimeo.com/…"
                  fullWidth
                  value={editLinkUrl}
                  disabled={editLoading}
                  onChange={(e) => { setEditLinkUrl(e.target.value); setEditError(''); setEditErrors((p) => ({ ...p, link: undefined })); }}
                  error={!!editErrors.link}
                  helperText={editErrors.link || 'Current URL is pre-filled — edit or leave unchanged'}
                  sx={videoInputSx}
                />
              </Box>
            ) : (
              <Box>
                <Typography sx={videoLabelSx}>Video file</Typography>
                <Box
                  component="label"
                  sx={{
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '20px',
                    border: '2px dashed', borderColor: editFile ? '#EB3349' : 'rgba(235,51,73,.45)',
                    bgcolor: editFile ? 'rgba(235,51,73,.07)' : 'transparent',
                    transition: 'all .2s ease',
                    '&:hover': { bgcolor: 'rgba(235,51,73,.07)', borderColor: '#EB3349' },
                  }}
                >
                  <Box
                    sx={{
                      width: 52, height: 52, borderRadius: '16px', display: 'grid', placeItems: 'center', color: '#fff', flexShrink: 0,
                      background: 'linear-gradient(135deg,#EB3349,#F45C43)',
                      boxShadow: '0 10px 20px rgba(235,51,73,.4), inset 0 1px 0 rgba(255,255,255,.45)',
                    }}
                  >
                    <CloudUploadRounded />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 900, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {editFile ? editFile.name : (editVideo?.originalName || editVideo?.filename || 'Current file')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {editFile ? 'New file selected — click to change' : 'Click to replace the video file (optional)'}
                      {videoQuota?.sizeLimit != null ? ` · up to ${videoQuota.sizeLimit} MB` : ''}
                    </Typography>
                  </Box>
                  <input
                    ref={editFileInputRef}
                    hidden
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.ogg,.mov,.avi,.mkv"
                    onChange={(e) => {
                      const nextFile = e.target.files?.[0] || null;
                      setEditFile(nextFile);
                      setEditError('');
                      setEditErrors((p) => ({ ...p, file: validateVideoFile(nextFile) || undefined }));
                    }}
                  />
                </Box>
                {editErrors.file && (
                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mt: 0.75 }}>{editErrors.file}</Typography>
                )}
              </Box>
            )}
            {/* Subtitle section — file videos only */}
            {(editVideo?.videoType || 'file') === 'file' && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '18px', p: 1.75, bgcolor: 'background.paper' }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <ClosedCaptionRounded sx={{ color: '#EB3349' }} />
                  <Typography sx={{ fontWeight: 800 }}>Subtitle file (.srt or .vtt)</Typography>
                </Stack>
                {editVideo?.subtitleUrl ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip label="Subtitle active" color="success" size="small" variant="outlined" />
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={subtitleUploading}
                      onClick={async () => {
                        if (!editVideo) return;
                        setSubtitleUploading(true);
                        try {
                          await deleteVideoSubtitle(editVideo._id);
                          setEditVideo((prev) => prev ? { ...prev, subtitleUrl: undefined } : prev);
                          setVideos((prev) => prev.map((v) => v._id === editVideo._id ? { ...v, subtitleUrl: undefined } : v));
                        } catch (e: any) {
                          Swal.fire('Error', e?.response?.data?.message || 'Failed to remove subtitle', 'error');
                        } finally {
                          setSubtitleUploading(false);
                        }
                      }}
                    >
                      Remove subtitle
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" mb={1}>No subtitle uploaded.</Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudUploadRounded />}
                    component="label"
                    disabled={subtitleUploading}
                  >
                    {editVideo?.subtitleUrl ? 'Replace subtitle' : 'Upload subtitle'}
                    <input
                      ref={subtitleFileInputRef}
                      hidden
                      type="file"
                      accept=".srt,.vtt"
                      onChange={(e) => setSubtitleFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {subtitleFile && (
                    <Typography variant="body2" sx={{ flex: 1 }} noWrap>{subtitleFile.name}</Typography>
                  )}
                  {subtitleFile && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={subtitleUploading}
                      onClick={async () => {
                        if (!editVideo || !subtitleFile) return;
                        setSubtitleUploading(true);
                        try {
                          const result = await uploadVideoSubtitle(editVideo._id, subtitleFile);
                          const newSubUrl = result.subtitleUrl || '';
                          setEditVideo((prev) => prev ? { ...prev, subtitleUrl: newSubUrl } : prev);
                          setVideos((prev) => prev.map((v) => v._id === editVideo._id ? { ...v, subtitleUrl: newSubUrl } : v));
                          setSubtitleFile(null);
                          if (subtitleFileInputRef.current) subtitleFileInputRef.current.value = '';
                        } catch (e: any) {
                          Swal.fire('Error', e?.response?.data?.message || 'Failed to upload subtitle', 'error');
                        } finally {
                          setSubtitleUploading(false);
                        }
                      }}
                    >
                      {subtitleUploading ? 'Uploading…' : 'Save subtitle'}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {editError && (
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
                {editError}
              </Typography>
            )}
          </Stack>
      </FormDialogShell>

      <FormDialogShell
        open={shareOpen}
        onClose={closeShareDialog}
        colors={VIDEO_COLORS}
        maxWidth="sm"
        icon={<IosShareRounded />}
        title="Share with friends"
        subtitle={shareVideo?.title || 'Untitled video'}
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeShareDialog} sx={videoCancelSx}>Cancel</Button>
            <Button
              onClick={handleShareVideo}
              disabled={shareLoading || shareSending}
              sx={gradientButtonSx(VIDEO_COLORS)}
            >
              {shareSending ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save Sharing'
              )}
            </Button>
          </>
        }
      >
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Selected friends will see this video on their Videos page and can watch it, but only
              the uploader can edit or delete it.
            </Typography>

            {shareLoading ? (
              <Stack spacing={1}>
                {[0, 1, 2].map((idx) => (
                  <Skeleton key={idx} variant="rounded" height={56} />
                ))}
              </Stack>
            ) : shareFriends.length === 0 ? (
              <Stack spacing={1}>
                <Typography color="text.secondary">
                  You do not have any active chats yet.
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/chat')}>
                  Open Chat
                </Button>
              </Stack>
            ) : (
              <List disablePadding>
                {shareFriends.map((friend) => {
                  const selected = shareSelectedIds.includes(friend._id);
                  const friendName = displayFriendName(friend);

                  return (
                    <ListItemButton
                      key={friend._id}
                      selected={selected}
                      onClick={() => toggleShareFriend(friend._id)}
                      sx={{
                        borderRadius: '16px', mb: 1, border: '1px solid',
                        borderColor: selected ? '#EB3349' : 'divider',
                        bgcolor: selected ? 'rgba(235,51,73,.08)' : 'background.paper',
                        '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'rgba(235,51,73,.08)' },
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        sx={{ mr: 0.5 }}
                      />
                      <Avatar sx={{ width: 36, height: 36, mr: 1.5 }}>
                        {(friendName.charAt(0) || 'U').toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={<Typography fontWeight={700}>{friendName}</Typography>}
                        secondary={friend.email || 'Active chat friend'}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Stack>
      </FormDialogShell>

      {/* Player */}
      {current && currentUrl && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            bgcolor: '#000',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.84), rgba(0,0,0,0.2))',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <IconButton
                onClick={closePlayer}
                aria-label="Go back"
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', width: 52, height: 52 }}
              >
                <ArrowBackRounded sx={{ fontSize: 32 }} />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap sx={{ fontSize: { xs: 18, md: 24 } }}>
                  {current.title || 'Untitled video'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.68)', fontSize: { xs: 13, md: 16 } }} noWrap>
                  {currentVideoType === 'link'
                    ? 'Protected link playback'
                    : current.originalName || current.filename || 'Protected file playback'}
                </Typography>
              </Box>
            </Stack>

            {currentVideoType === 'file' && (
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={toggleFullscreen}
                  startIcon={isFullscreen ? <FullscreenExitRounded sx={{ fontSize: 30 }} /> : <FullscreenRounded sx={{ fontSize: 30 }} />}
                  sx={{
                    minWidth: 0,
                    px: 2,
                    py: 1,
                    borderRadius: 1.5,
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    fontSize: 16,
                    fontWeight: 700,
                    gap: 0.75,
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                  }}
                >
                  {isFullscreen ? 'Exit' : 'Fullscreen'}
                </Button>
              </Stack>
            )}
          </Box>

          {currentVideoType === 'link' ? (
            <>
              <LinearProgress
                sx={{
                  height: 3,
                  bgcolor: 'rgba(255,255,255,0.07)',
                  opacity: linkPlayerLoading ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  '& .MuiLinearProgress-bar': { bgcolor: '#EF5350' },
                }}
              />
              <Box sx={{ position: 'relative', flex: 1, bgcolor: '#000' }}>
                {linkPlayerLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.54)' }} />
                  </Box>
                )}
                <iframe
                  src={toEmbedUrl(currentUrl)}
                  title={current.title}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#000' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  onLoad={() => setLinkPlayerLoading(false)}
                />
              </Box>
            </>
          ) : (
            <Box
              ref={videoWrapperRef}
              onMouseMove={showControlsTemporarily}
              onClick={handlePlayerSurfaceClick}
              onDoubleClick={handleDoubleClick}
              onTouchEnd={handleTouchEnd}
              sx={{
                position: 'relative',
                flex: 1,
                width: '100%',
                bgcolor: '#000',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'manipulation',
                // Fullscreen: the pointer disappears with the controls after a moment of inactivity.
                cursor: isFullscreen && isPlaying && !controlsVisible ? 'none' : 'default',
                '&:fullscreen': { width: '100vw', height: '100vh' },
                '&:-webkit-full-screen': { width: '100vw', height: '100vh' },
              }}
            >
              {playerError ? (
                <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ px: 3, textAlign: 'center' }}>
                  <ErrorOutlineRounded sx={{ fontSize: 84, color: 'rgba(255,255,255,0.45)' }} />
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {playerError}
                  </Typography>
                  <Button onClick={closePlayer} sx={{ color: 'rgba(255,255,255,0.88)', textTransform: 'none' }}>
                    Go back
                  </Button>
                </Stack>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    crossOrigin="anonymous"
                    src={absoluteUrl(currentUrl)}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      background: '#000',
                    }}
                    playsInline
                    onPlay={() => {
                      setIsPlaying(true);
                      setVideoBuffering(false);
                      startControlsHideTimer();
                    }}
                    onPause={() => {
                      setIsPlaying(false);
                      setControlsVisible(true);
                      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
                    }}
                    onEnded={() => {
                      setIsPlaying(false);
                      setControlsVisible(true);
                      if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
                    }}
                    onWaiting={() => setVideoBuffering(true)}
                    onSeeking={() => setVideoBuffering(true)}
                    onCanPlay={() => setVideoBuffering(false)}
                    onPlaying={() => {
                      setIsPlaying(true);
                      setVideoBuffering(false);
                    }}
                    onTimeUpdate={() => {
                      const player = videoRef.current;
                      if (!player || isScrubbing) return;
                      setVideoCurrentTime(player.currentTime);
                      const nextProgress = player.duration ? (player.currentTime / player.duration) * 100 : 0;
                      setVideoProgress(nextProgress);
                      setScrubProgress(nextProgress);
                    }}
                    onLoadedMetadata={() => {
                      const player = videoRef.current;
                      if (!player) return;
                      setVideoDuration(player.duration || 0);
                      setVideoCurrentTime(player.currentTime || 0);
                      const nextProgress = player.duration ? (player.currentTime / player.duration) * 100 : 0;
                      setVideoProgress(nextProgress);
                      setScrubProgress(nextProgress);
                    }}
                    onError={() => {
                      const mediaError = videoRef.current?.error;
                      const message = mediaError?.message || 'Unable to play this video.';
                      setPlayerError(message);
                      setVideoBuffering(false);
                      setIsPlaying(false);
                    }}
                  >
                    {subtitleBlobUrl && (
                      <track
                        key={subtitleBlobUrl}
                        kind="subtitles"
                        src={subtitleBlobUrl}
                        srcLang="en"
                        label="Subtitles"
                        default={subtitlesEnabled}
                      />
                    )}
                    Your browser does not support the video tag.
                  </video>

                  {videoBuffering && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.54)' }} />
                    </Box>
                  )}

                  {seekIndicator && (
                    <Box
                      key={seekIndicator.key}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        [seekIndicator.direction === 'backward' ? 'left' : 'right']: { xs: 18, sm: 42 },
                        transform: 'translateY(-50%)',
                        px: 1.75,
                        py: 1.25,
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        opacity: seekIndicator.visible ? 1 : 0,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none',
                      }}
                    >
                      {seekIndicator.direction === 'backward' ? (
                        <Replay10Rounded sx={{ color: '#fff' }} />
                      ) : (
                        <Forward10Rounded sx={{ color: '#fff' }} />
                      )}
                      <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                        {seekIndicator.direction === 'backward' ? '-10s' : '+10s'}
                      </Typography>
                    </Box>
                  )}

                  {!isPlaying && !videoBuffering && (
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        togglePlayPause();
                      }}
                      aria-label="Play video"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 120,
                        height: 120,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.42)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.58)' },
                      }}
                    >
                      <PlayArrowRounded sx={{ fontSize: 80 }} />
                    </IconButton>
                  )}

                  <Box
                    onClick={(event) => event.stopPropagation()}
                    onTouchEnd={(event) => event.stopPropagation()}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      px: { xs: 1.5, sm: 2 },
                      pb: { xs: 1.5, sm: 2 },
                      pt: 7,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.84))',
                      opacity: controlsVisible ? 1 : 0,
                      pointerEvents: controlsVisible ? 'auto' : 'none',
                      transition: 'opacity 0.24s ease',
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Button
                          onClick={togglePlayPause}
                          startIcon={isPlaying ? <PauseRounded sx={{ fontSize: 32 }} /> : <PlayArrowRounded sx={{ fontSize: 32 }} />}
                          sx={{
                            minWidth: 0,
                            px: 2.25,
                            py: 1.1,
                            borderRadius: 1.5,
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.12)',
                            fontSize: 18,
                            fontWeight: 700,
                            gap: 1,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                          }}
                        >
                          {isPlaying ? 'Pause' : 'Play'}
                        </Button>
                        <Box
                          sx={{
                            px: 2.25,
                            py: 1.1,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(255,255,255,0.12)',
                            color: '#fff',
                            fontSize: 18,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                          }}
                        >
                          {formatTime(videoCurrentTime)} / {formatTime(videoDuration)}
                        </Box>
                        <Button
                          onClick={toggleFullscreen}
                          startIcon={isFullscreen ? <FullscreenExitRounded sx={{ fontSize: 30 }} /> : <FullscreenRounded sx={{ fontSize: 30 }} />}
                          sx={{
                            minWidth: 0,
                            px: 2.25,
                            py: 1.1,
                            borderRadius: 1.5,
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.4)',
                            fontSize: 18,
                            fontWeight: 700,
                            gap: 1,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                          }}
                        >
                          {isFullscreen ? 'Exit Full' : 'Fullscreen'}
                        </Button>
                        {/* Volume control — beside fullscreen */}
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 190 }}>
                          <IconButton
                            onClick={toggleMute}
                            aria-label="Toggle mute"
                            sx={{ color: '#fff', width: 44, height: 44, p: 0, flexShrink: 0, '& svg': { fontSize: 30 } }}
                          >
                            {getVolumeIcon()}
                          </IconButton>
                          <Slider
                            size="small"
                            value={videoMuted ? 0 : Math.round(videoVolume * 100)}
                            onChange={handleVolumeChange}
                            aria-label="Volume"
                            sx={{
                              color: '#fff',
                              width: 140,
                              m: 0,
                              height: 5,
                              '& .MuiSlider-rail': { color: 'rgba(255,255,255,0.28)', opacity: 1, height: 5 },
                              '& .MuiSlider-track': { border: 'none', height: 5 },
                              '& .MuiSlider-thumb': {
                                width: 20,
                                height: 20,
                                bgcolor: '#fff',
                                '&::before': { boxShadow: 'none' },
                              },
                            }}
                          />
                          <SoundSettingsButton
                            settings={sound.settings}
                            onChange={sound.update}
                            onReset={sound.reset}
                            supported={sound.supported}
                            sx={{ color: '#fff', p: 0.75, '& svg': { fontSize: 30 } }}
                          />
                        </Stack>
                        {/* CC toggle — after volume, only when subtitle available */}
                        {currentSubtitleUrl && (
                          <IconButton
                            onClick={() => setSubtitlesEnabled((v) => !v)}
                            aria-label="Toggle subtitles"
                            title={subtitlesEnabled ? 'Turn off subtitles' : 'Turn on subtitles'}
                            sx={{
                              color: subtitlesEnabled ? '#EF5350' : '#fff',
                              width: 44,
                              height: 44,
                              p: 0,
                              '& svg': { fontSize: 30 },
                            }}
                          >
                            {subtitlesEnabled
                              ? <ClosedCaptionRounded fontSize="small" />
                              : <ClosedCaptionDisabledRounded fontSize="small" />}
                          </IconButton>
                        )}
                      </Stack>

                      <Slider
                        size="small"
                        value={isScrubbing ? scrubProgress : videoProgress}
                        onChange={handleSeekChange}
                        onChangeCommitted={handleSeekCommit}
                        aria-label="Video seek"
                        sx={{
                          color: '#EF5350',
                          height: 12,
                          py: '14px', // taller hit area so seeking is easy
                          px: 0,
                          cursor: 'pointer',
                          '& .MuiSlider-rail': { color: 'rgba(255,255,255,0.3)', opacity: 1, height: 12 },
                          '& .MuiSlider-track': { border: 'none', height: 12 },
                          '& .MuiSlider-thumb': {
                            width: 26,
                            height: 26,
                            bgcolor: '#fff',
                            '&::before': { boxShadow: 'none' },
                            '&:hover, &.Mui-focusVisible, &.Mui-active': {
                              boxShadow: '0 0 0 8px rgba(255,0,0,0.25)',
                            },
                          },
                        }}
                      />
                    </Stack>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
